/**
 * Expedição — painel da separação/embalagem da matriz (Tiny matriz + prazo/status do Mercado Livre).
 *
 * Layout aprovado em `mockup_gerencial.html` (22/10/2026): o dia de hoje (não usa filtros) → filtros → cards →
 * barras por situação → barras por canal → atenção + por pessoa → embalados nos últimos 30 dias.
 * Todo número de pedidos abre a gaveta com a lista (abrirLista). Só fala com o serviço `services/expedicao` (8110);
 * as contas de negócio (faixa, alertas, dia de hoje, ritmo, série) estão lá, testadas.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, Button, Card, CardContent, CircularProgress, Grid, Stack, Typography } from '@mui/material'
import { DomainPageShell } from '../components/DomainPageShell'
import { KpiCard } from '../components/KpiCard'
import { usePanelAccess } from '../permissions/usePanelAccess'
import type { AbrirLista, DadosExpedicao, Fonte } from './expedicao/types'
import { carregar, carregarLinkTv, pedirAtualizacao } from './expedicao/fonte'
import { FILTRO_INICIAL, baseAtencao, baseRanking, doEtapa, etapasVisiveis, ROTULO_ETAPA, ROTULO_FAIXA, faltam, horaSP, passa, resumoPedidos, type Filtro } from './expedicao/logic'
import { DiaDeHoje } from './expedicao/DiaDeHoje'
import { Filtros } from './expedicao/Filtros'
import { BarrasCanal, BarrasSituacao } from './expedicao/Barras'
import { Atencao } from './expedicao/Atencao'
import { PorPessoa } from './expedicao/PorPessoa'
import { Serie30 } from './expedicao/Serie30'
import { Gaveta, type ListaAberta } from './expedicao/Gaveta'

const RECARGA_MS = 60_000
const ESPERA_ATUALIZAR_MS = 20_000
const FONTE_VELHA_MS = 15 * 60_000
const ROTULO_FONTE: Record<string, string> = {
  tiny: 'Tiny', ml: 'ML', shopee: 'Shopee', shein: 'Shein', tiktok: 'TikTok', amazon: 'Amazon', site: 'Site',
  ml_retidos: 'ML (aguardando liberação)', shopee_decora: 'Shopee Decora',
}
const rotuloFonte = (f: string) => ROTULO_FONTE[f] ?? f.charAt(0).toUpperCase() + f.slice(1)

function avisoFonte(f: Fonte, agora: number): string | null {
  const nome = rotuloFonte(f.fonte)
  if (!f.ok) return `${nome} com erro na última leitura${f.erro ? `: ${f.erro}` : ''}. O painel segue com o prazo e o status da última leitura boa; pedidos novos desse canal podem aparecer sem prazo.`
  if (!f.lido_em || agora - new Date(f.lido_em).getTime() > FONTE_VELHA_MS) {
    return `${nome} sem leitura há mais de 15 min${f.lido_em ? ` (última às ${horaSP(f.lido_em)})` : ''}.`
  }
  return null
}

export function ExpedicaoPage() {
  const { canWrite } = usePanelAccess()
  const [dados, setDados] = useState<DadosExpedicao | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [filtro, setFiltro] = useState<Filtro>(FILTRO_INICIAL)
  const [lista, setLista] = useState<ListaAberta | null>(null)
  const [msg, setMsg] = useState<{ tom: 'info' | 'error'; texto: string } | null>(null)
  const [atualizando, setAtualizando] = useState(false)
  const espera = useRef<number | null>(null)

  const recarregar = useCallback(async () => {
    try {
      setDados(await carregar())
      setErro(null)
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e))
    }
  }, [])
  useEffect(() => {
    void recarregar()
    const id = window.setInterval(() => void recarregar(), RECARGA_MS)
    return () => {
      window.clearInterval(id)
      if (espera.current != null) window.clearTimeout(espera.current)
    }
  }, [recarregar])

  const abrirLista: AbrirLista = useCallback((titulo, pedidos) => setLista({ titulo, pedidos }), [])

  const atualizarAgora = async () => {
    setAtualizando(true)
    try {
      await pedirAtualizacao()
      setMsg({ tom: 'info', texto: 'Leitura do Tiny e do ML pedida. A tela recarrega em 20 s.' })
      espera.current = window.setTimeout(() => {
        setAtualizando(false)
        void recarregar()
      }, ESPERA_ATUALIZAR_MS)
    } catch (e) {
      setAtualizando(false)
      setMsg({ tom: 'error', texto: `Não foi possível pedir a atualização: ${e instanceof Error ? e.message : String(e)}` })
    }
  }

  const abrirTv = async () => {
    // abre a aba já no clique (senão o navegador bloqueia o pop-up depois do await) e só então põe o endereço
    const aba = window.open('about:blank', '_blank')
    try {
      const url = await carregarLinkTv()
      if (!url) throw new Error('chave da TV não configurada no serviço')
      if (aba) {
        aba.opener = null
        aba.location.href = url
      } else window.location.href = url
    } catch (e) {
      aba?.close()
      setMsg({ tom: 'error', texto: `Não foi possível abrir o modo TV: ${e instanceof Error ? e.message : String(e)}` })
    }
  }

  const filtrados = useMemo(() => (dados ? dados.pedidos.filter((p) => passa(p, filtro)) : []), [dados, filtro])
  const baseDaAtencao = useMemo(() => (dados ? baseAtencao(dados.pedidos, filtro) : []), [dados, filtro])
  const baseDoRanking = useMemo(() => (dados ? baseRanking(dados.pedidos, filtro) : []), [dados, filtro])
  const embaladores = useMemo(() => {
    const m = new Map<string, string>()
    for (const p of dados?.pedidos ?? []) if (p.embalador_id) m.set(p.embalador_id, p.embalador_nome)
    return [...m].map(([id, nome]) => ({ id, nome })).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
  }, [dados])

  const periodo = filtro.faixa.size
    ? [...filtro.faixa].map((f) => ROTULO_FAIXA[f]).join(' + ')
    : filtro.de || filtro.ate ? 'intervalo de datas' : 'todas as datas'
  const pend = faltam(filtrados)
  // "Aguardando liberação" e as etapas da Decora só aparecem quando existem (flags desligadas = tela de antes)
  const etapas = dados ? etapasVisiveis(dados.pedidos) : []
  // 5 etapas: uma linha só (como antes); mais que isso, 5 cards por linha
  const colEtapa = etapas.length > 5 ? 2.4 : 9.6 / Math.max(etapas.length, 1)
  const agora = Date.now()
  const avisos = dados ? dados.fontes.map((f) => avisoFonte(f, agora)).filter((x): x is string => !!x) : []
  const atualizado = dados?.fontes.map((f) => `${rotuloFonte(f.fonte)} ${f.lido_em ? horaSP(f.lido_em) : '—'}`).join(' · ')

  return (
    <DomainPageShell title="Expedição" subtitle="Matriz · Tiny (separação) + data máxima de envio dos marketplaces · somente leitura">
      <Stack spacing={2.5}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" color="text.secondary">
            {dados ? `Atualizado: ${atualizado || 'ainda sem leitura'}` : 'Carregando…'}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="outlined" disabled={atualizando} onClick={() => void atualizarAgora()}>
            {atualizando ? 'Atualizando…' : 'Atualizar agora'}
          </Button>
          <Button size="small" variant="outlined" onClick={() => void abrirTv()}>Modo TV</Button>
        </Stack>

        {erro && <Alert severity="error">Não foi possível carregar a Expedição: {erro}</Alert>}
        {dados && !dados.fontes.length && <Alert severity="warning">O serviço ainda não fez a primeira leitura do Tiny e do ML.</Alert>}
        {avisos.map((a) => <Alert key={a} severity="warning">{a}</Alert>)}
        {msg && <Alert severity={msg.tom} onClose={() => setMsg(null)}>{msg.texto}</Alert>}
        {!dados && !erro && <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>}

        {dados && (
          <>
            <DiaDeHoje dados={dados} abrirLista={abrirLista} />
            <Filtros filtro={filtro} onChange={setFiltro} embaladores={embaladores} pedidos={dados.pedidos} />

            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, md: 2.4 }}>
                <KpiCard label="Faltam embalar" value={String(pend.length)} selected
                  hint={`${periodo} · de ${resumoPedidos(filtrados)}`}
                  onClick={() => abrirLista(`Faltam embalar · ${periodo}`, pend)} />
              </Grid>
              {etapas.map((s) => {
                const ps = doEtapa(filtrados, s)
                return (
                  <Grid key={s} size={{ xs: 6, md: colEtapa }}>
                    <KpiCard label={ROTULO_ETAPA[s]} value={String(ps.length)}
                      hint={`${filtrados.length ? Math.round((ps.length / filtrados.length) * 100) : 0}% do total`}
                      onClick={() => abrirLista(`${ROTULO_ETAPA[s]} · ${periodo}`, ps)} />
                  </Grid>
                )
              })}
            </Grid>

            <Card variant="outlined"><CardContent>
              <Typography variant="h6">Situação da separação</Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>Pedidos dentro dos filtros, por situação no Tiny · "Enviado" = embalado que o marketplace já deu como coletado/enviado.</Typography>
              <BarrasSituacao pedidos={filtrados} sufixo={periodo} abrirLista={abrirLista} etapas={etapas} />
            </CardContent></Card>

            <Card variant="outlined"><CardContent>
              <Typography variant="h6">Por marketplace</Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                Cada pedaço da barra é uma situação (Enviado = embalado já coletado). O número da direita é quanto falta embalar.
              </Typography>
              <BarrasCanal pedidos={filtrados} sufixo={periodo} abrirLista={abrirLista} />
            </CardContent></Card>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card variant="outlined" sx={{ height: '100%' }}><CardContent>
                  <Typography variant="h6">Pontos de atenção</Typography>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>Pedidos que precisam de alguém olhar · ignora a data máx.; respeita os demais filtros.</Typography>
                  <Atencao pedidos={baseDaAtencao} canceladosEmbalados={dados.cancelados_embalados} parados={dados.parados_tiny} abrirLista={abrirLista}
                    comRetidos={dados.pedidos.some((p) => p.situacao === 0)} />
                </CardContent></Card>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Card variant="outlined" sx={{ height: '100%' }}><CardContent>
                  <Typography variant="h6">Embalados hoje por pessoa</Typography>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>Usuário embalador do Tiny · ignora a data máx.; respeita os demais filtros.</Typography>
                  <PorPessoa pedidos={baseDoRanking} hoje={dados.dia.data} podeEditar={canWrite('expedicao')}
                    abrirLista={abrirLista} onSalvo={() => void recarregar()} />
                </CardContent></Card>
              </Grid>
            </Grid>

            <Card variant="outlined"><CardContent>
              <Typography variant="h6">Embalados por dia · últimos 30 dias</Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                Data do checkout no Tiny. Dias sem expediente em cinza. Clique num dia para ver os pedidos · só o filtro de marketplace vale aqui.
              </Typography>
              <Serie30 serie={dados.serie_30} canais={filtro.canal} abrirLista={abrirLista} />
            </CardContent></Card>
          </>
        )}
      </Stack>
      <Gaveta lista={lista} onClose={() => setLista(null)} />
    </DomainPageShell>
  )
}
